import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import type { Game } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PriceHistory } from "@/components/games/PriceHistory";
import { PriceDropAlertButton } from "@/components/games/PriceDropAlertButton";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";

const GAME_QUERY = gql`
  query GamePriceHistory($id: ID!) {
    game(id: $id) {
      id
      slug
      title
      imageUrl
      thumbnailUrl
      yearPublished
      priceTrackingSince
      priceHistory {
        date
        price
        source
        shopName
        condition
        listingUrl
        listingName
        editionHint
      }
      priceEnvelope {
        date
        min
        med
        max
        shops {
          shopName
          price
          listingUrl
          listingName
        }
      }
      priceHistorySummary {
        allTimeLow
        allTimeLowAt
        last30dLow
        last90dLow
        currentLow
        currentLowShopName
        trackingSince
        lastChangeAt
        totalObservations
      }
      shopListings {
        id
        shopName
        listingName
        listingUrl
        price
        priceUpdatedAt
      }
      myWishlist {
        id
        maxPrice
        notifyOnPriceDrop
      }
    }
  }
`;

function formatEur(value: number | null | undefined) {
  if (value == null) return null;
  return `${value.toFixed(2)} €`;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("lt-LT", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function buyVerdict(currentLow: number | null, allTimeLow: number | null) {
  if (currentLow == null || allTimeLow == null) return null;
  const diff = +(currentLow - allTimeLow).toFixed(2);
  if (diff <= 1) {
    return {
      tone: "good" as const,
      text: "Dabar geras laikas pirkti — kaina labai arti visų laikų pigiausios.",
    };
  }
  return {
    tone: "wait" as const,
    text: `Dabartinė kaina ${diff.toFixed(2)} € aukštesnė už visų laikų pigiausią. Verta palaukti arba užsisakyti pranešimą apie kainos kritimą.`,
  };
}

export function GamePriceHistoryPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId?.split("-")[0];
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["gamePriceHistory", id],
    queryFn: () =>
      graphqlClient.request<{ game: Game | null }>(GAME_QUERY, { id }),
    enabled: !!id,
  });

  const game = data?.game;

  useEffect(() => {
    if (!game?.slug || !rawId) return;
    const canonical = `${game.id}-${game.slug}`;
    if (rawId !== canonical) {
      navigate(`/games/${canonical}/kainos`, { replace: true });
    }
  }, [game?.id, game?.slug, rawId, navigate]);

  usePageTitle(game ? `${game.title} – kainos istorija` : null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Žaidimas nerastas</h1>
        <p className="mt-2 text-gray-600">
          Galbūt jis pašalintas arba nuoroda neteisinga.
        </p>
        <Link to="/games" className="mt-6 inline-block text-primary-700 underline">
          Grįžti į žaidimų katalogą
        </Link>
      </div>
    );
  }

  const summary = game.priceHistorySummary ?? null;
  const verdict = buyVerdict(summary?.currentLow ?? null, summary?.allTimeLow ?? null);
  const gameUrl = `/games/${game.id}-${game.slug}`;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <nav className="mb-4 text-sm text-gray-500" aria-label="Naršymo kelias">
        <Link to="/games" className="hover:text-gray-700">Žaidimai</Link>
        <span className="mx-2">›</span>
        <Link to={gameUrl} className="hover:text-gray-700">{game.title}</Link>
        <span className="mx-2">›</span>
        <span aria-current="page">Kainos</span>
      </nav>

      <header className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="h-32 w-32 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100">
          {game.imageUrl ? (
            <img src={game.imageUrl} alt={game.title} className="h-full w-full object-cover" />
          ) : (
            <GamePlaceholder />
          )}
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">
            {game.title} – kainos istorija
          </h1>
          {summary?.totalObservations ? (
            <p className="mt-2 text-sm text-gray-600">
              Sekame {summary.totalObservations} kainų pokyčius
              {summary.trackingSince ? ` nuo ${formatDate(summary.trackingSince)}` : ""}.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-600">
              Šio žaidimo kainų istorijos dar neturime.
            </p>
          )}
          <div className="mt-4">
            <PriceDropAlertButton
              gameId={game.id}
              shopListings={game.shopListings}
              currentTargetPrice={
                game.myWishlist?.notifyOnPriceDrop ? game.myWishlist.maxPrice ?? null : null
              }
              alertEnabled={!!game.myWishlist?.notifyOnPriceDrop}
            />
          </div>
        </div>
      </header>

      {summary && (
        <section className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryStat
            label="Šiuo metu nuo"
            value={formatEur(summary.currentLow)}
            subtitle={summary.currentLowShopName ?? undefined}
          />
          <SummaryStat
            label="Visų laikų pigiausia"
            value={formatEur(summary.allTimeLow)}
            subtitle={summary.allTimeLowAt ? formatDate(summary.allTimeLowAt) ?? undefined : undefined}
          />
          <SummaryStat
            label="Pigiausia per 30 d."
            value={formatEur(summary.last30dLow) ?? "—"}
          />
          <SummaryStat
            label="Pigiausia per 90 d."
            value={formatEur(summary.last90dLow) ?? "—"}
          />
        </section>
      )}

      {verdict && (
        <p
          className={`mb-6 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
            verdict.tone === "good"
              ? "bg-emerald-50 text-emerald-800"
              : "bg-amber-50 text-amber-800"
          }`}
        >
          {verdict.text}
        </p>
      )}

      <PriceHistory
        points={game.priceHistory}
        envelope={game.priceEnvelope ?? []}
        shopListings={game.shopListings}
        trackingSince={game.priceTrackingSince}
      />

      <p className="mt-10 text-sm text-gray-600">
        <Link to={gameUrl} className="text-primary-700 underline">
          ← Grįžti į {game.title} aprašymą ir skelbimus
        </Link>
      </p>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: string | null;
  subtitle?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value ?? "—"}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}
